import { SchemaService, ServerService, TcoService } from '@amzn/awscat-portfolio-assessment-service-client';
import { AwsEC2InstanceType, DeserializedServer } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Button, FormField, Header, Input, SpaceBetween, Tabs, TabsProps } from '@amzn/awsui-components-react';
import { useContext, useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router';
import { useIsMounted } from 'react-tidy';

import { HelpPanelTopicOptions } from '../../common/Constants';
import DashboardsLocalStorage, { Dashboard } from '../../common/DashboardLocalStorage';
import { constants } from '../../common/Localization';
import { MatchParam } from '../../common/types';
import { CookieConsentContext } from '../../contexts/CookieConsentContext';
import { HelpPanelContext } from '../../contexts/HelpPanelContext';
import { PortfolioContext } from '../../contexts/PortfolioContext';
import ConfirmationModal from '../common/ConfirmationModal';
import FeatureNotAvailable from '../common/FeatureNotAvailable';
import { SchemaPropertyMap } from '../portfolio-data-asset/types';
import DashboardTab from './dashboard/DashboardTab';

type CreateDashboardState = {
    dashboardName: string;
    visible: boolean;
}

type Props = FlashContextInterface;

const serverService = new ServerService();
const schemaService = new SchemaService();
const tcoService = new TcoService();

function DashboardPage({ flash }: Props) {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId',
    });
    const portfolioId = match?.params.portfolioId || '';
    const { portfolio } = useContext(PortfolioContext);

    const [ loading, setLoading ] = useState(false);
    const [ dashboardTabs, setDashboardTabs ] = useState<TabsProps.Tab[]>([]);
    const [ activeDashboardTab, setActiveDashboardTab ] = useState<string>('default');
    const [ createDashboardState, setCreateDashboardState ] = useState<CreateDashboardState>({
        dashboardName: '',
        visible: false,
    });
    const [ removeDashboardVisible, setRemoveDashboardVisible ] = useState(false);
    const [ serverData, setServerData ] = useState<DeserializedServer[]>([]);
    const [ serverSchemaMap, setServerSchemaMap ] = useState<SchemaPropertyMap>();
    const [ instanceTypeData, setInstanceTypeData ] = useState<AwsEC2InstanceType[]>([]);
    const [ hasCookieConsent, setHasCookieConsent ] = useState(false);
    const { panelHandler } = useContext(HelpPanelContext);

    const { cookie, getLocalStorage, setLocalStorage } = useContext(CookieConsentContext);

    const isMounted = useIsMounted();

    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.DASHBOARDS);
    }, [ panelHandler ]);

    useEffect(() => {
        setHasCookieConsent(cookie.functional);
    }, [ cookie ]);
    
    useEffect(() => {
        const dashboardNames = DashboardsLocalStorage.enumerateDashboards(portfolioId, getLocalStorage);
        if (!dashboardNames.length) {
            dashboardNames.push('default');
        }
        setDashboardTabs(dashboardNames.map(name => ({
            id: name,
            label: name,
        })));
        setActiveDashboardTab(dashboardNames[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ portfolioId ]);

    const loadServerInstanceAndSchemaData = async () => {
        setLoading(true);
        try {
            const servers = await serverService.list(portfolioId);
            if (!isMounted()) {
                return;
            }
            setServerData(servers);

            const serverSchemas = await schemaService.listAttributes('servers', portfolioId);
            const serverHeaders: SchemaPropertyMap = {};
            serverSchemas.forEach(serverSchema => {
                serverHeaders[serverSchema.value] = serverSchema;
            });
            setServerSchemaMap(serverHeaders);

            if (portfolio.defaultTcoAssessmentId) {
                const awsEC2Assessment = await tcoService.getAwsEC2(portfolio.defaultTcoAssessmentId);
                if (isMounted()) {
                    setInstanceTypeData(awsEC2Assessment.output.aws.ec2.instanceTypes);
                }
            }
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, '');
            }
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        loadServerInstanceAndSchemaData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ portfolio.defaultTcoAssessmentId ]);

    const onConfirmCreate = () => {
        const newDashboard: Dashboard = { charts: [] };
        const newDashboardName = createDashboardState.dashboardName;
        DashboardsLocalStorage.saveDashboard(portfolioId, newDashboardName, newDashboard, getLocalStorage, setLocalStorage);
        dashboardTabs.push({ id: newDashboardName, label: newDashboardName });
        setDashboardTabs(dashboardTabs);
        setActiveDashboardTab(newDashboardName);
        setCreateDashboardState({ dashboardName: '', visible: false });
    };

    const onConfirmRemove = () => {
        const dashboardTabsCopy = [...dashboardTabs];
        const activeIndex = dashboardTabsCopy.findIndex(tab => tab.id === activeDashboardTab);
        dashboardTabsCopy.splice(activeIndex, 1);
        setDashboardTabs(dashboardTabsCopy);
        setActiveDashboardTab(dashboardTabsCopy[0].id);
        setRemoveDashboardVisible(false);
        DashboardsLocalStorage.deleteDashboard(portfolioId, activeDashboardTab, getLocalStorage, setLocalStorage);
    };
    return (
        <div>
            <Header variant='h1' actions={
                <SpaceBetween direction='horizontal' size='m'>
                    <Button 
                        variant='primary' 
                        onClick={() => setCreateDashboardState({dashboardName: '', visible: true})}
                        disabled={!hasCookieConsent}
                    >
                        {constants.CREATE_DASHBOARD}
                    </Button>
                    <Button 
                        disabled={activeDashboardTab === 'default' || !hasCookieConsent} 
                        onClick={() => setRemoveDashboardVisible(true)}
                    >
                        {constants.REMOVE_DASHBOARD}
                    </Button>
                </SpaceBetween>
            }>{constants.DASHBOARDS}</Header>
            { hasCookieConsent ? (
                <div>
                    <Tabs
                        tabs={dashboardTabs}
                        activeTabId={activeDashboardTab}
                        onChange={e => setActiveDashboardTab(e.detail.activeTabId)} />
                    <DashboardTab
                        activeDashboard={activeDashboardTab}
                        data={{servers: serverData, instanceTypes: instanceTypeData}}
                        dataLoading={loading}
                        schemaPropertyMap={serverSchemaMap}
                    />
                </div>
            ) : (
                <FeatureNotAvailable />
            ) }
            
            <ConfirmationModal
                visible={createDashboardState.visible}
                header={constants.CREATE_DASHBOARD}
                onConfirm={onConfirmCreate}
                onDismiss={() => setCreateDashboardState({dashboardName: '', visible: false})}
                confirmButton={constants.CREATE_BUTTON_TEXT}
                confirmDisabled={!createDashboardState.dashboardName}
            >
                <FormField label={constants.DASHBOARD_NAME} description={constants.A_NAME_FOR_NEW_DASHBOARD}>
                    <Input
                        value={createDashboardState.dashboardName}
                        onChange={e => setCreateDashboardState({dashboardName: e.detail.value, visible: true})}
                        placeholder={constants.DASHBOARD_NAME} />
                </FormField>
            </ConfirmationModal>
            {removeDashboardVisible ? (
                <ConfirmationModal
                    visible
                    header={constants.REMOVE_DASHBOARD}
                    warning={constants.REMOVE_DASHBOARD_WARNING}
                    friction={'remove'}
                    onConfirm={onConfirmRemove}
                    onDismiss={() => setRemoveDashboardVisible(false)}
                    confirmButton={constants.REMOVE_BUTTON_TEXT}
                />
            ): null}
        </div>
    );
}

export default withFlashContext(DashboardPage);
